import React, { useState } from "react";
import { ArrowRight, Mail, Globe, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHero, SectionHeader } from "@/components/Bits";
import { LIFESTYLE } from "@/lib/brand";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been received.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        overline="Contact"
        title="We'd love to hear from you."
        subtitle="Inquiries, partnerships, media — every message is read by our team."
        image={LIFESTYLE.executiveWoman}
      />

      <section className="ess-section bg-ess-paper">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader overline="Direct" title="Reach the Society." />
            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-ess-gold-deep mt-1" />
                <div>
                  <p className="ess-overline">Email</p>
                  <a
                    href="mailto:exitstrategysociety@gmail.com"
                    className="font-display text-xl text-ess-emerald hover:text-ess-gold-deep transition"
                    data-testid="contact-email"
                  >
                    exitstrategysociety@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe size={18} className="text-ess-gold-deep mt-1" />
                <div>
                  <p className="ess-overline">Web</p>
                  <a
                    href="https://exitstrategysociety.com"
                    className="font-display text-xl text-ess-emerald hover:text-ess-gold-deep transition"
                    data-testid="contact-website"
                  >
                    exitstrategysociety.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-ess-gold-deep mt-1" />
                <div>
                  <p className="ess-overline">Founder</p>
                  <p className="font-display text-xl text-ess-emerald">
                    Dr. Phelesia Foster, PhD
                  </p>
                  <p className="text-ess-slate text-sm">
                    The Exit Strategy Society — a DBA of 4ever Wellness LLC.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="contact-form"
          >
            <div>
              <label className="ess-overline">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="contact-name"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div>
              <label className="ess-overline">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="contact-email-input"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="ess-overline">Subject</label>
              <input
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                data-testid="contact-subject"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="ess-overline">Message</label>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="contact-message"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none resize-none"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="bg-ess-emerald text-white px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-ess-emerald-dark transition disabled:opacity-60 inline-flex items-center gap-3"
              >
                {submitting ? "Sending…" : "Send Message"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
